// Explicit .ts extension: the tick helpers below are unit-tested by Node
// directly, and Node's type stripping requires it.
import { cardinal, type Point, type Reading, type StationHistory } from './types.ts';
import { trackChartPointer } from './chartPointer.ts';
import { positionChartTip } from './chartTip.ts';
import { gridLineBudget, gridTicks, tickDecimals } from './lib/gridTicks.ts';

/**
 * Stacked, time-aligned panels for one station: wind, water level, waves,
 * temperature — whichever the station actually reports.
 *
 * They share one time domain, one set of hourly ticks, and one crosshair, which
 * is the entire point: an ebb under a building westerly is a thing you *see*
 * when the tide sits directly beneath the wind. Each measure keeps its own
 * panel and its own scale — never two y-axes on one plot, which would invent a
 * correlation out of arbitrary scale alignment.
 */

const W = 336;
const PAD = { top: 12, right: 12, bottom: 20, left: 36 };
/* A buoy stacks four panels, so the per-panel budget is tight by necessity —
   the card has to stay inside the viewport without an inner scroll. */
const PANEL_GAP = 14;
const DIR_STRIP_H = 20;
const WIND_PANEL_H = 84;
const SUB_PANEL_H = 46;

const INK_MUTED = '#898781';
const INK_SECONDARY = '#c3c2b7';
const GRID = '#2c2c2a';
const AXIS = '#383835';
const SURFACE = '#1a1a19';

/** Documented dark-mode categorical steps: slot 1 blue, slot 2 orange. */
const SERIES_1 = '#3987e5';
const SERIES_2 = '#d95926';

const SVG_NS = 'http://www.w3.org/2000/svg';

function el(name: string, attrs: Record<string, string | number> = {}): SVGElement {
  const node = document.createElementNS(SVG_NS, name);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

const HOUR_MS = 3_600_000;

/**
 * Whole clock hours inside the window, in local time — the same clock the labels
 * and the tooltip use. Aligned to the hour rather than evenly subdivided, so a
 * tick means "10 PM", not "1/24th of the way along".
 */
export function hourTicks(t0: number, t1: number, stepHours: number): number[] {
  const first = new Date(t0);
  first.setMinutes(0, 0, 0);
  if (first.getTime() < t0) first.setHours(first.getHours() + 1);
  // Land the series on multiples of the step so ticks don't drift by window.
  while (first.getHours() % stepHours !== 0) first.setHours(first.getHours() + 1);

  const out: number[] = [];
  for (const d = new Date(first); d.getTime() <= t1; d.setHours(d.getHours() + stepHours)) {
    out.push(d.getTime());
  }
  return out;
}

/**
 * Smallest hour step from `allowed` that keeps ticks at least `minPx` apart.
 * The window is 24h today, but history depth is a parameter — at 7 days an
 * hourly tick would be sub-pixel, so the spacing decides, not the constant.
 */
export function stepForSpacing(
  spanMs: number,
  innerW: number,
  minPx: number,
  allowed: number[],
): number {
  for (const h of allowed) {
    if (((h * HOUR_MS) / spanMs) * innerW >= minPx) return h;
  }
  return allowed[allowed.length - 1];
}

export const TICK_STEPS = [1, 2, 3, 6, 12, 24];

interface SeriesSpec {
  label: string;
  points: Point[];
  color: string;
  width: number;
}

interface Panel {
  label: string;
  unit: string;
  height: number;
  series: SeriesSpec[];
  /** Filled area between two series, used for the gust range. */
  band?: { lower: Point[]; upper: Point[]; color: string; label: string };
  /** Wind starts at zero; temperature and water level should not. */
  zeroBased: boolean;
  decimals: number;
}

/** A rounded axis range that always contains the data. */
export function niceDomain(values: number[], zeroBased: boolean): [number, number] {
  if (values.length === 0) return [0, 1];
  const lo = Math.min(...values);
  const hi = Math.max(...values);

  if (zeroBased) {
    const top = hi <= 10 ? 10 : hi <= 20 ? 20 : hi <= 30 ? 30 : Math.ceil(hi / 10) * 10;
    return [0, top];
  }

  const pad = Math.max((hi - lo) * 0.15, 0.5);
  const step = hi - lo > 20 ? 5 : 1;
  return [Math.floor((lo - pad) / step) * step, Math.ceil((hi + pad) / step) * step];
}

/** Builds the panels a station can actually fill, in reading order. */
export function panelsFor(history: StationHistory): Panel[] {
  const panels: Panel[] = [];
  const wind = history.wind;

  // A wave buoy carries no anemometer, so an empty wind series is a fact about
  // the station rather than a gap in its data. Pushing the panel anyway drew an
  // empty axis above the measurements it does report.
  if (wind.length > 0) panels.push({
    label: 'Wind',
    unit: 'kt',
    height: WIND_PANEL_H,
    zeroBased: true,
    decimals: 1,
    series: [{
      label: 'Sustained',
      points: wind.map((r) => ({ t: r.t, v: r.speedKt })),
      color: SERIES_1,
      width: 2,
    }],
    band: {
      lower: wind.map((r) => ({ t: r.t, v: r.speedKt })),
      upper: wind.map((r) => ({ t: r.t, v: r.gustKt ?? r.speedKt })),
      color: SERIES_1,
      label: 'Gust',
    },
  });

  if (history.waterLevel?.length) {
    const series: SeriesSpec[] = [
      { label: 'Observed', points: history.waterLevel, color: SERIES_1, width: 2 },
    ];
    // Predicted is a reference, not a rival identity, so it takes muted ink
    // rather than burning a categorical slot.
    if (history.waterLevelPredicted?.length) {
      series.push({
        label: 'Predicted',
        points: history.waterLevelPredicted,
        color: INK_MUTED,
        width: 1.5,
      });
    }
    panels.push({
      label: 'Water level', unit: 'ft', height: SUB_PANEL_H, zeroBased: false, decimals: 1, series,
    });
  }

  if (history.waveHeight?.length) {
    panels.push({
      label: 'Wave height', unit: 'ft', height: SUB_PANEL_H, zeroBased: true, decimals: 1,
      series: [{ label: 'Significant', points: history.waveHeight, color: SERIES_1, width: 2 }],
    });
  }

  const temps: SeriesSpec[] = [];
  if (history.waterTemp?.length) {
    temps.push({ label: 'Water', points: history.waterTemp, color: SERIES_1, width: 2 });
  }
  if (history.airTemp?.length) {
    temps.push({ label: 'Air', points: history.airTemp, color: SERIES_2, width: 2 });
  }
  if (temps.length) {
    // Name the panel for what it actually holds. Most stations report only one
    // of the two — a bare "Temperature" left the reader guessing whether 58°F
    // was the sea or the air, which are rarely close here.
    const label = temps.length === 1 ? `${temps[0].label} temperature` : 'Temperature';
    panels.push({
      label, unit: '°F', height: SUB_PANEL_H, zeroBased: false, decimals: 1, series: temps,
    });
  }

  return panels;
}

function allPoints(p: Panel): Point[] {
  return [...p.series.flatMap((s) => s.points), ...(p.band ? p.band.upper : [])];
}

export function renderCharts(host: HTMLElement, history: StationHistory): void {
  host.textContent = '';

  const wind = history.wind;
  const panels = panelsFor(history);

  // Judged across every panel, not the wind alone: a station that reports only
  // wave height has a chart worth drawing, and keying this on wind reported
  // "not enough history" over a full day of measurements.
  if (!panels.some((p) => allPoints(p).length >= 2)) {
    const empty = document.createElement('p');
    empty.className = 'chart-empty';
    empty.textContent = 'Not enough history to plot.';
    host.append(empty);
    return;
  }

  const innerW = W - PAD.left - PAD.right;

  // One time domain across every panel, taken from the union of all series so a
  // slower-reporting sensor cannot shrink the window under the wind.
  const times = panels.flatMap((p) => allPoints(p)).map((pt) => pt.t.getTime());
  const t0 = Math.min(...times);
  const t1 = Math.max(...times);
  const span = Math.max(t1 - t0, 1);

  const xAt = (ms: number) => PAD.left + ((ms - t0) / span) * innerW;

  const tickStep = stepForSpacing(span, innerW, 7, TICK_STEPS);
  const labelStep = stepForSpacing(span, innerW, 58, TICK_STEPS.filter((h) => h >= tickStep));
  const ticks = hourTicks(t0, t1, tickStep);

  // Vertical layout: each panel gets a top offset, then the direction strip and
  // the shared axis sit below the stack.
  const tops: number[] = [];
  let cursor = PAD.top;
  for (const p of panels) {
    tops.push(cursor);
    cursor += p.height + PANEL_GAP;
  }
  const stackBottom = cursor - PANEL_GAP;
  const stripY = stackBottom + PAD.bottom + DIR_STRIP_H / 2;
  const totalH = stackBottom + PAD.bottom + DIR_STRIP_H + 6;

  const domains = panels.map((p) => niceDomain(allPoints(p).map((pt) => pt.v), p.zeroBased));

  const yAt = (i: number, v: number) => {
    const [lo, hi] = domains[i];
    const frac = hi === lo ? 0.5 : (v - lo) / (hi - lo);
    return tops[i] + (1 - Math.min(Math.max(frac, 0), 1)) * panels[i].height;
  };

  // Typed as the SVG root so the tip can use getScreenCTM for exact placement.
  const svg = el('svg', {
    viewBox: `0 0 ${W} ${totalH}`,
    width: '100%',
    height: totalH,
    role: 'img',
    'aria-label':
      `${panels.map((p) => p.label).join(', ')} over the last ` +
      `${Math.round(span / HOUR_MS)} hours`,
  }) as SVGSVGElement;

  // --- Per-panel frame: label, min/max ticks, hairline top and baseline ---
  panels.forEach((panel, i) => {
    const [lo, hi] = domains[i];

    // Round intermediate values, not just the bounds: reading 17 kt off a
    // panel whose only marks were 0 and 30 meant interpolating by eye.
    const ticks = gridTicks(lo, hi, gridLineBudget(panel.height));
    const decimals = tickDecimals(ticks);

    for (const v of ticks) {
      // The baseline carries a touch more weight; the rest stay recessive so
      // the grid never competes with the series drawn over it.
      const isBase = v === lo;
      svg.append(el('line', {
        x1: PAD.left, x2: W - PAD.right, y1: yAt(i, v), y2: yAt(i, v),
        stroke: isBase ? AXIS : GRID, 'stroke-width': 1,
      }));
      const label = el('text', {
        x: PAD.left - 6, y: yAt(i, v) + 3.5,
        'text-anchor': 'end', fill: INK_MUTED, 'font-size': 9,
        'font-variant-numeric': 'tabular-nums',
      });
      label.textContent = v.toFixed(decimals);
      svg.append(label);
    }

    const title = el('text', {
      x: PAD.left, y: tops[i] - 3, fill: INK_SECONDARY, 'font-size': 9, 'font-weight': 600,
    });
    title.textContent = `${panel.label} (${panel.unit})`;
    svg.append(title);
  });

  // --- Hour ticks, drawn once against the whole stack ---
  for (const ms of ticks) {
    const tx = xAt(ms);
    const labelled = new Date(ms).getHours() % labelStep === 0;

    if (labelled) {
      // Labelled hours get a hairline through every panel so a moment in time
      // can be traced from the wind down to the tide.
      panels.forEach((p, i) => {
        svg.append(el('line', {
          x1: tx, x2: tx, y1: tops[i], y2: tops[i] + p.height,
          stroke: GRID, 'stroke-width': 1,
        }));
      });
    }

    svg.append(el('line', {
      x1: tx, x2: tx,
      y1: stackBottom,
      y2: stackBottom + (labelled ? 6 : 3),
      stroke: INK_MUTED, 'stroke-width': 1,
      opacity: labelled ? 1 : 0.75,
    }));

    if (!labelled) continue;

    const at = new Date(ms);
    const text = el('text', {
      x: tx, y: stackBottom + 15,
      'text-anchor': 'middle', fill: INK_MUTED, 'font-size': 9,
    });
    // Midnight names the day instead of repeating "12 AM", which is how the
    // window gets its date context now that the ends are unlabelled.
    text.textContent = at.getHours() === 0
      ? new Intl.DateTimeFormat([], { weekday: 'short' }).format(at)
      : new Intl.DateTimeFormat([], { hour: 'numeric' }).format(at);
    svg.append(text);
  }

  // --- Data ---
  panels.forEach((panel, i) => {
    if (panel.band) {
      const top = panel.band.upper.map((p) => `${xAt(p.t.getTime())},${yAt(i, p.v)}`).join(' ');
      const bottom = panel.band.lower
        .slice().reverse()
        .map((p) => `${xAt(p.t.getTime())},${yAt(i, p.v)}`).join(' ');
      svg.append(el('polygon', {
        points: `${top} ${bottom}`, fill: panel.band.color, 'fill-opacity': 0.22,
      }));
    }

    for (const s of panel.series) {
      if (s.points.length === 0) continue;
      svg.append(el('polyline', {
        points: s.points.map((p) => `${xAt(p.t.getTime())},${yAt(i, p.v)}`).join(' '),
        fill: 'none', stroke: s.color, 'stroke-width': s.width,
        'stroke-linejoin': 'round', 'stroke-linecap': 'round',
      }));

      // Endpoint marker on the primary series only — never a dot per point.
      const last = s.points[s.points.length - 1];
      if (s === panel.series[0]) {
        svg.append(el('circle', {
          cx: xAt(last.t.getTime()), cy: yAt(i, last.v), r: 3.5,
          fill: s.color, stroke: SURFACE, 'stroke-width': 2,
        }));
      }
    }
  });

  // --- Direction strip: rotation carries bearing, so no cyclic color ramp ---
  const nearestWind = (targetMs: number): Reading =>
    wind.reduce((best, r) =>
      Math.abs(r.t.getTime() - targetMs) < Math.abs(best.t.getTime() - targetMs) ? r : best,
    );

  // Only where there is wind to point: a wave buoy reports no bearing, and the
  // strip would otherwise be an empty row labelled "dir". `nearestWind` reduces
  // without a seed, so it would throw on the empty series in any case.
  if (wind.length > 0) {
    const dirStep = stepForSpacing(span, innerW, 22, TICK_STEPS.filter((h) => h >= tickStep));
    for (const ms of hourTicks(t0, t1, dirStep)) {
      const r = nearestWind(ms);
      if (r.dirDeg === null) continue;
      svg.append(el('path', {
        d: 'M0,-5 L3,3 L0,1 L-3,3 Z',
        fill: INK_SECONDARY,
        // dirDeg is where wind comes FROM; the glyph points downwind.
        transform: `translate(${xAt(ms)},${stripY}) rotate(${r.dirDeg + 180})`,
      }));
    }
    const stripLabel = el('text', {
      x: PAD.left - 6, y: stripY + 3.5,
      'text-anchor': 'end', fill: INK_MUTED, 'font-size': 8,
    });
    stripLabel.textContent = 'dir';
    svg.append(stripLabel);
  }

  // --- Shared hover layer: one crosshair across every panel ---
  const crosshair = el('line', {
    y1: PAD.top, y2: stackBottom, stroke: INK_MUTED, 'stroke-width': 1, opacity: 0,
  });
  svg.append(crosshair);

  const cursors = panels.map((panel, i) =>
    panel.series.map((s) => {
      const dot = el('circle', {
        r: 3.5, fill: s.color, stroke: SURFACE, 'stroke-width': 2, opacity: 0,
      });
      svg.append(dot);
      return { dot, series: s, panel: i };
    }),
  ).flat();

  const wrap = document.createElement('div');
  wrap.className = 'chart-wrap';
  const tip = document.createElement('div');
  tip.className = 'chart-tip';
  tip.hidden = true;
  wrap.append(svg, tip);

  const tipTimeFmt = new Intl.DateTimeFormat([], {
    weekday: 'short', hour: 'numeric', minute: '2-digit',
  });

  /** Nearest sample, but only if it is close enough to be the same moment. */
  const nearestPoint = (points: Point[], targetMs: number): Point | null => {
    if (points.length === 0) return null;
    const best = points.reduce((a, b) =>
      Math.abs(b.t.getTime() - targetMs) < Math.abs(a.t.getTime() - targetMs) ? b : a,
    );
    return Math.abs(best.t.getTime() - targetMs) <= 45 * 60_000 ? best : null;
  };

  /** The panel the pointer is in, so the tip can step off that one. */
  const panelAt = (clientY: number): { top: number; bottom: number } => {
    const ctm = svg.getScreenCTM();
    const svgY = ctm && ctm.d !== 0 ? (clientY - ctm.f) / ctm.d : tops[0];

    let index = panels.findIndex(
      (p, i) => svgY >= tops[i] - PANEL_GAP / 2 && svgY <= tops[i] + p.height + PANEL_GAP / 2,
    );
    // Below the stack is the axis or the direction strip: treat it as the last
    // panel so the tip still steps off it.
    if (index === -1) index = svgY < tops[0] ? 0 : panels.length - 1;

    return { top: tops[index], bottom: tops[index] + panels[index].height };
  };

  const show = (targetMs: number, clientY: number | null) => {
    // The crosshair normally snaps to the nearest wind observation. Without an
    // anemometer there is nothing to snap to, so it follows the cursor instead.
    const anchor = wind.length > 0 ? nearestWind(targetMs) : null;
    const ms = anchor ? anchor.t.getTime() : targetMs;
    const tx = xAt(ms);

    crosshair.setAttribute('x1', String(tx));
    crosshair.setAttribute('x2', String(tx));
    crosshair.setAttribute('opacity', '1');

    const rows: string[] = [`<strong>${tipTimeFmt.format(new Date(ms))}</strong>`];

    for (const c of cursors) {
      const p = nearestPoint(c.series.points, ms);
      if (!p) {
        c.dot.setAttribute('opacity', '0');
        continue;
      }
      c.dot.setAttribute('cx', String(xAt(p.t.getTime())));
      c.dot.setAttribute('cy', String(yAt(c.panel, p.v)));
      c.dot.setAttribute('opacity', '1');

      const panel = panels[c.panel];
      // Series first reads naturally either way: "Water temperature",
      // "Observed water level", "Sustained wind".
      const name = panel.series.length > 1 || panel.band
        ? `${c.series.label} ${panel.label.toLowerCase()}`
        : panel.label;
      rows.push(`<span>${name} ${p.v.toFixed(panel.decimals)} ${panel.unit}</span>`);
    }

    const gust = nearestPoint(
      wind.filter((r) => r.gustKt !== null).map((r) => ({ t: r.t, v: r.gustKt! })), ms,
    );
    if (gust) rows.push(`<span>Wind gust ${gust.v.toFixed(1)} kt</span>`);
    if (anchor && anchor.dirDeg !== null) {
      rows.push(`<span>From ${cardinal(anchor.dirDeg)} ${Math.round(anchor.dirDeg)}°</span>`);
    }

    tip.innerHTML = rows.join('');
    tip.hidden = false;
    // After unhiding and filling: placement needs the tip's real size.
    if (clientY !== null) positionChartTip(svg, wrap, tip, tx, panelAt(clientY));
  };

  const hide = () => {
    crosshair.setAttribute('opacity', '0');
    for (const c of cursors) c.dot.setAttribute('opacity', '0');
    tip.hidden = true;
  };

  trackChartPointer(svg, {
    onMove: (clientX, clientY) => {
      const box = svg.getBoundingClientRect();
      const px = ((clientX - box.left) / box.width) * W;
      const frac = Math.min(Math.max((px - PAD.left) / innerW, 0), 1);
      show(t0 + frac * span, clientY);
    },
    onEnd: hide,
  });

  host.append(wrap);

  const legend = document.createElement('p');
  legend.className = 'chart-legend';
  // One legend serves the whole stack, so every entry is fully qualified —
  // a bare "Water" sitting beside "Observed" and "Predicted" gave no clue which
  // panel either belonged to.
  legend.innerHTML = panels
    .flatMap((p) => [
      ...p.series.map((s) =>
        `<span class="legend-item"><i class="swatch-line" style="background:${s.color}"></i>` +
        `${p.series.length > 1 ? `${s.label} ${p.label.toLowerCase()}` : p.label}</span>`),
      ...(p.band
        ? [`<span class="legend-item"><i class="swatch-band" style="background:${p.band.color};` +
           `opacity:.4"></i>${p.band.label}</span>`]
        : []),
    ])
    .join('');
  host.append(legend);
}

/**
 * The table-view twin. Every value in every panel is reachable without colour or
 * hover — the charts enhance this, they never gate it.
 *
 * Rows are the union of all series timestamps, since CO-OPS publishes each
 * product on its own cadence and they do not always coincide.
 */
export function renderTable(host: HTMLElement, history: StationHistory): void {
  host.textContent = '';
  const fmt = new Intl.DateTimeFormat([], { hour: 'numeric', minute: '2-digit' });

  const columns: Array<{ head: string; get: (ms: number) => string }> = [
    { head: 'Wind kt', get: (ms) => valueAt(history.wind.map((r) => ({ t: r.t, v: r.speedKt })), ms, 1) },
    {
      head: 'Gust',
      get: (ms) => valueAt(
        history.wind.filter((r) => r.gustKt !== null).map((r) => ({ t: r.t, v: r.gustKt! })), ms, 1,
      ),
    },
    {
      head: 'Dir',
      get: (ms) => {
        const r = history.wind.find((x) => x.t.getTime() === ms);
        return r?.dirDeg == null ? '—' : `${cardinal(r.dirDeg)} ${Math.round(r.dirDeg)}°`;
      },
    },
  ];

  if (history.waterLevel?.length) {
    columns.push({ head: 'Level ft', get: (ms) => valueAt(history.waterLevel!, ms, 2) });
  }
  if (history.waveHeight?.length) {
    columns.push({ head: 'Wave ft', get: (ms) => valueAt(history.waveHeight!, ms, 1) });
  }
  if (history.waterTemp?.length) {
    columns.push({ head: 'Water °F', get: (ms) => valueAt(history.waterTemp!, ms, 1) });
  }
  if (history.airTemp?.length) {
    columns.push({ head: 'Air °F', get: (ms) => valueAt(history.airTemp!, ms, 1) });
  }

  const stamps = [...new Set([
    ...history.wind.map((r) => r.t.getTime()),
    ...(history.waterLevel ?? []).map((p) => p.t.getTime()),
    ...(history.waterTemp ?? []).map((p) => p.t.getTime()),
    ...(history.airTemp ?? []).map((p) => p.t.getTime()),
    ...(history.waveHeight ?? []).map((p) => p.t.getTime()),
  ])].sort((a, b) => b - a);

  const table = document.createElement('table');
  table.className = 'reading-table';
  table.innerHTML =
    `<thead><tr><th scope="col">Time</th>${
      columns.map((c) => `<th scope="col">${c.head}</th>`).join('')
    }</tr></thead>`;

  const body = document.createElement('tbody');
  for (const ms of stamps) {
    const row = document.createElement('tr');
    row.innerHTML =
      `<td>${fmt.format(new Date(ms))}</td>` +
      columns.map((c) => `<td>${c.get(ms)}</td>`).join('');
    body.append(row);
  }
  table.append(body);
  host.append(table);
}

/** Exact-timestamp lookup — a blank cell is honest about a differing cadence. */
function valueAt(points: Point[], ms: number, decimals: number): string {
  const hit = points.find((p) => p.t.getTime() === ms);
  return hit ? hit.v.toFixed(decimals) : '—';
}
